if (!customElements.get("bundle-modal")) {
  customElements.define(
    "bundle-modal",
    class bundleModal extends Drawer {
      constructor() {
        super();

        this.contentContainer = this.querySelector(
          ".product-content-container",
        );

        this.applyButton = this.querySelector(".apply-button");
        if (!this.applyButton) return;

        this.boundApply = this.applyChange.bind(this);
        this.applyButton.addEventListener("click", this.boundApply);
      }

      disconnectedCallback() {
        super.disconnectedCallback();

        if (this.applyButton && this.boundApply)
          this.applyButton.removeEventListener("click", this.boundApply);

        if (this.quantityInput && this.boundQuantityChange)
          this.quantityInput.removeEventListener(
            "change",
            this.boundQuantityChange,
          );

        if (this.variantPickerSelects && this.boundOnChange)
          this.variantPickerSelects.removeEventListener(
            "change",
            this.boundOnChange,
          );
      }

      /**
       * Initializes event listeners for variant selection and quantity changes.
       * @returns {void}
       */
      initEventListen() {
        this.variantPickerSelects = this.querySelector(
          ".product-variant-selects",
        );
        if (!this.variantPickerSelects) return;

        // Unbind first then rebind to prevent duplicate listeners
        if (this.boundOnChange) {
          this.variantPickerSelects.removeEventListener(
            "change",
            this.boundOnChange,
          );
        }
        this.boundOnChange = this.onChange.bind(this);
        this.variantPickerSelects.addEventListener(
          "change",
          this.boundOnChange,
        );

        // Quantity change listener
        this.quantityInput = this.querySelector("input[name='quantity']"); // Quantity input
        const displayCountElement = this.querySelector(`.quantity-count`);
        if (displayCountElement && this.quantityInput) {
          if (this.boundQuantityChange) {
            this.quantityInput.removeEventListener(
              "change",
              this.boundQuantityChange,
            );
          } // Unbind first

          this.boundQuantityChange = (event) => {
            displayCountElement.textContent = event.target.value || 1;
          };
          this.quantityInput.addEventListener(
            "change",
            this.boundQuantityChange,
          );
        }
      }

      show(opener) {
        super.show(opener);

        // Hide sticky header to prevent it from overlapping the drawer
        const stickyHeader = document.getElementById("Header-Sticky");
        if (stickyHeader) stickyHeader.hide?.();

        if (this.openedBy) {
          this.productUrl = this.openedBy.getAttribute("data-product-url");
          this.variantId = parseInt(
            this.openedBy.getAttribute("data-variant-id"),
          ); // Variant ID

          this.bundleProductCard = this.openedBy.closest(
            ".bundle-product-card",
          ); // Product card
          if (this.bundleProductCard)
            this.bundleQuantityInput = this.bundleProductCard.querySelector(
              "input[name='quantity']",
            ); // Quantity input
        } else if (window.Shopify.designMode) {
          this.productUrl = this.dataset.productUrl;
          this.variantId = this.dataset.variantId;
        }
        if (!this.variantId || !this.productUrl) return;

        this.variantData = null;
        this.showPlaceholder();

        if (this.fetchController) this.fetchController.abort();
        this.fetchController = new AbortController();

        webvista
          .fetchHtml(
            `${this.productUrl}?section_id=${this.dataset.section}&variant=${this.variantId}`,
            this.fetchController.signal,
          )
          .then((html) => {
            const sourceContentHtml = html.querySelector(
              "template.template-product",
            )?.innerHTML;

            if (sourceContentHtml)
              this.contentContainer.innerHTML = sourceContentHtml;

            // Parse current variant data
            this.getCurrentVariant();

            // Set quantity selector boundaries
            const quantity = this.querySelector("quantity-input");
            if (quantity) quantity.setQuantityBoundaries();

            if (this.bundleQuantityInput) {
              // Sync quantity from bundle card
              this.quantityInput = this.querySelector("input[name='quantity']");
              if (this.quantityInput)
                this.updateQuantity(
                  this.quantityInput,
                  this.bundleQuantityInput,
                );
            }

            // Initialize event listeners
            this.initEventListen();

            // Re-initialize lazy images and tooltips
            webvista.initLazyImages();
            webvista.initToolTips();
          })
          .catch((error) => {
            // console.log(error);
            this.hide();
          });
      }

      hide() {
        if (this.fetchController) this.fetchController.abort(); // Abort pending fetch requests

        super.hide();
      }

      /**
       * Applies the selected variant change back to the bundle product card.
       * @returns {void}
       */
      applyChange() {
        if (!this.openedBy) return;

        // Get variant display elements
        const variantTitle = this.querySelector(
          ".product-info-card .info-options",
        );
        const variantPrice = this.querySelector(
          ".product-info-card .info-price .price",
        );
        const variantImage = this.querySelector(
          ".product-info-card .info-media",
        );

        // Update variant-related data on the bundle card
        this.openedBy.setAttribute("data-variant-id", this.currentVariant.id);
        this.openedBy.textContent = variantTitle.textContent;
        this.bundleProductCard.querySelector("input[name='id']").value =
          this.currentVariant.id;
        this.bundleProductCard.querySelector(".card-media .media").innerHTML =
          variantImage.innerHTML;
        this.bundleProductCard
          .querySelector(".card-price .price")
          .replaceWith(variantPrice.cloneNode(true));

        // Sync quantity back to bundle card
        if (this.bundleQuantityInput && this.quantityInput) {
          this.updateQuantity(this.bundleQuantityInput, this.quantityInput);
        }

        // Dispatch change event to trigger total recalculation
        this.bundleProductCard.dispatchEvent(
          new Event("change", { bubbles: true }),
        );

        this.hide();
      }

      /**
       * Syncs quantity value between two input elements.
       * @param {HTMLElement} targetElement Target quantity input
       * @param {HTMLElement} sourceElement Source quantity input
       */
      updateQuantity(targetElement, sourceElement) {
        if (targetElement && sourceElement) {
          targetElement.value = sourceElement.value;

          // Manually trigger the change event
          targetElement.dispatchEvent(new Event("change", { bubbles: false })); // Non-bubbling, only for syncing display quantity
        }
      }

      /**
       * Handles variant option and quantity change events.
       */
      onChange(event) {
        const inputElement = event.target;
        if (!inputElement) return;

        if (
          (inputElement.tagName === "INPUT" && inputElement.type === "radio") ||
          inputElement.tagName === "SELECT"
        ) {
          this.updateOptions(event.target); // Collect currently selected option value IDs
          this.renderProductInfo(event.target);
        }
      }

      /**
       * Collects selected option value IDs from all variant selectors.
       * Returns an array of option value IDs.
       * @param target The changed input/select element
       */
      updateOptions(target) {
        if (target.tagName === "SELECT" && target.selectedOptions.length) {
          const selectedOption = Array.from(target.options).find((option) =>
            option.getAttribute("selected"),
          );
          if (selectedOption) selectedOption.removeAttribute("selected");

          target.selectedOptions[0].setAttribute("selected", "selected");
        }

        this.optionValueIds = Array.from(
          this.querySelectorAll(
            "select option:checked, fieldset input:checked",
          ),
        )
          .map((element) => element.dataset.optionValueId)
          .filter(Boolean);
      }

      /**
       * Parses the current variant JSON data from the embedded script tag.
       */
      getCurrentVariant() {
        const json = this.querySelector(
          "script[data-selected-variant]",
        ).textContent;
        this.currentVariant = !!json ? JSON.parse(json) : null;
      }

      /**
       * Re-renders product info by fetching updated section HTML.
       * @param target The changed input element (used for focus restoration)
       */
      renderProductInfo(target = null) {
        this.startLoading();

        if (this.fetchController) this.fetchController.abort();
        this.fetchController = new AbortController();

        webvista
          .fetchHtml(
            `${this.productUrl}?section_id=${this.dataset.section}&option_values=${this.optionValueIds.join(",")}`,
            this.fetchController.signal,
          )
          .then((html) => {
            const sourceContent = html.querySelector(
              "template.template-product",
            )?.content;
            if (!sourceContent) return;

            const needReplaceSelectors = [
              ".product-info-card .info-media",
              ".product-info-card .info-options",
              ".product-info-card .info-price .price",
              ".product-variant-selects",
            ];
            needReplaceSelectors.forEach((selector) => {
              const source = sourceContent.querySelector(selector);
              const target = this.querySelector(selector);

              if (source && target) target.replaceWith(source);
            });

            // Parse current variant data
            this.getCurrentVariant();

            // Re-initialize event listeners
            this.initEventListen();

            // Re-initialize lazy images and tooltips
            webvista.initLazyImages();
            webvista.initToolTips();
          })
          .catch((error) => {
            // console.log(error);
            this.hide();
          })
          .finally(() => {
            if (target) document.querySelector(`#${target.id}`)?.focus();
            this.endLoading();
          });
      }

      /**
       * Shows the loading placeholder content.
       */
      showPlaceholder() {
        this.contentContainer.innerHTML = this.querySelector(
          "template.template-placeholder",
        ).innerHTML;
      }

      startLoading() {
        if (this.variantPickerSelects)
          this.variantPickerSelects.setAttribute("aria-disabled", "true");
        if (this.applyButton) {
          this.applyButton.classList.add("disabled");
          this.applyButton.setAttribute("aria-disabled", "true");
        }
      }

      endLoading() {
        if (this.variantPickerSelects)
          this.variantPickerSelects.removeAttribute("aria-disabled");
        if (this.applyButton) {
          this.applyButton.classList.remove("disabled");
          this.applyButton.removeAttribute("aria-disabled");
        }
      }
    },
  );
}
